import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { FormControl, FormField, FormItem, FormMessage } from "design-system/organisms/Form"
import { ChevronsLeftRightEllipsis, Plug } from "lucide-react"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import {
  getCompatibleTriggers,
  isSupportedChannel,
} from "modules/campaigns/lib/getCompatibleTriggers"
import { GoogleIcon } from "modules/shared/icons/google"
import { InstagramIcon } from "modules/shared/icons/instagram"
import { MessengerIcon } from "modules/shared/icons/messenger"
import { OutlookIcon } from "modules/shared/icons/outlook"
import { WhatsappIcon } from "modules/shared/icons/whatsapp"
import { ComponentType, Fragment, useMemo } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { ChannelEnum, ChannelTriggersMap, type CampaignTriggerType, type Channel } from "schema"
import { z } from "zod"

type CampaignFormValues = z.infer<typeof UpdateCampaignSchemaFrontend>

type SupportedChannel = keyof typeof ChannelTriggersMap

type ChannelDisplay = {
  icon: ComponentType<{ className?: string }>
  labelKey: string
  descriptionKey: string
}

const channelDisplay: Record<SupportedChannel, ChannelDisplay> = {
  [ChannelEnum.Whatsapp]: {
    icon: WhatsappIcon,
    labelKey: "campaigns.channel.whatsapp.label",
    descriptionKey: "campaigns.channel.whatsapp.description",
  },
  [ChannelEnum.Web]: {
    icon: ChevronsLeftRightEllipsis,
    labelKey: "campaigns.channel.web.label",
    descriptionKey: "campaigns.channel.web.description",
  },
  [ChannelEnum.Instagram]: {
    icon: InstagramIcon,
    labelKey: "campaigns.channel.instagram.label",
    descriptionKey: "campaigns.channel.instagram.description",
  },
  [ChannelEnum.Messenger]: {
    icon: MessengerIcon,
    labelKey: "campaigns.channel.messenger.label",
    descriptionKey: "campaigns.channel.messenger.description",
  },
  [ChannelEnum.Gmail]: {
    icon: GoogleIcon,
    labelKey: "campaigns.channel.gmail.label",
    descriptionKey: "campaigns.channel.gmail.description",
  },
  [ChannelEnum.Outlook]: {
    icon: OutlookIcon,
    labelKey: "campaigns.channel.outlook.label",
    descriptionKey: "campaigns.channel.outlook.description",
  },
  [ChannelEnum.Api]: {
    icon: Plug,
    labelKey: "campaigns.channel.api.label",
    descriptionKey: "campaigns.channel.api.description",
  },
}

export function ChannelSelect({ isEditable }: { isEditable: boolean }) {
  const { t } = useTranslation()
  const form = useFormContext<CampaignFormValues>()
  const flow = form.watch("flow")
  const channel = form.watch("channel")

  const visibleChannels = useMemo(() => {
    return (Object.keys(ChannelTriggersMap) as SupportedChannel[]).filter(
      (ch) => !flow || getCompatibleTriggers(ch, flow).length > 0,
    )
  }, [flow])

  return (
    <FormField
      control={form.control}
      name="channel"
      render={({ field }) => {
        const selectedDisplay = isSupportedChannel(channel) ? channelDisplay[channel] : null
        return (
          <FormItem className="flex flex-col gap-2">
            <FormControl>
              <Select
                key={channel ?? "empty"}
                value={channel ?? undefined}
                onValueChange={(value) => {
                  const newChannel = value as Channel
                  field.onChange(newChannel, { shouldDirty: true })
                  const currentTrigger = form.getValues("trigger")
                  if (
                    currentTrigger &&
                    isSupportedChannel(newChannel) &&
                    !(ChannelTriggersMap[newChannel] as readonly CampaignTriggerType[]).includes(
                      currentTrigger,
                    )
                  ) {
                    form.setValue("trigger", null, { shouldDirty: true })
                    form.setValue("triggerIntegration", null, { shouldDirty: true })
                  }
                }}
                disabled={!isEditable}
              >
                <SelectTrigger className="h-auto py-3 [&>span]:line-clamp-none [&>span]:min-w-0 [&>span]:w-full">
                  <SelectValue placeholder={t("campaigns.channel.placeholder")}>
                    {selectedDisplay ? (
                      <div className="flex flex-col gap-1.5 text-left min-w-0 w-full">
                        <div className="flex items-center gap-2 min-w-0">
                          <selectedDisplay.icon className="w-5 h-5 shrink-0" />
                          <span className="text-sm font-medium truncate">
                            {t(selectedDisplay.labelKey)}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground truncate">
                          {t(selectedDisplay.descriptionKey)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">
                        {t("campaigns.channel.placeholder")}
                      </span>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {visibleChannels.map((ch, index) => {
                    const display = channelDisplay[ch]
                    return (
                      <Fragment key={ch}>
                        <SelectItem value={ch}>
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <display.icon className="w-5 h-5" />
                              <span className="text-base font-medium">{t(display.labelKey)}</span>
                            </div>
                            <span className="text-sm text-muted-foreground">
                              {t(display.descriptionKey)}
                            </span>
                          </div>
                        </SelectItem>
                        {index < visibleChannels.length - 1 && <SelectSeparator />}
                      </Fragment>
                    )
                  })}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}
